import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useCompositionStore } from "../../stores/compositionStore.js";
import { assetUrl } from "../../infra/api.js";
import { PlaybackEngine } from "./engine.js";

export interface UsePlaybackEngine {
  play: () => void;
  pause: () => void;
  seek: (ms: number) => void;
  currentMs: number;
  totalMs: number;
  /**
   * Live ref to the engine — exposed so the read-only `useStageEnergy` hook can
   * poll `getActiveSection()` per animation frame without re-creating its own
   * engine. `null` between mount/unmount. Never used to drive transport.
   */
  engineRef: MutableRefObject<PlaybackEngine | null>;
}

// Wires the (otherwise dead) PlaybackEngine into the editor: one AudioContext
// and one engine per editor mount, fed by the sections of the composition
// store. Playback is ephemeral UI state — it never produces domain events.
export function usePlaybackEngine(): UsePlaybackEngine {
  const projectId = useCompositionStore((s) => s.projectId);
  const sections = useCompositionStore((s) => s.audioState.sections);
  const playbackState = useCompositionStore((s) => s.playback.state);

  const engineRef = useRef<PlaybackEngine | null>(null);
  // Tracks the load key (asset hash + stem set) loaded per section so an
  // unrelated event (which hands us a fresh `sections` array reference) does not
  // re-fetch and re-decode every clip's audio. The stem set is part of the key
  // because the engine wires one gain/EQ chain per stem at load time — a section
  // that gains a stem must be reloaded so `applyStemMix` has a node to drive.
  const loadedKeys = useRef(new Map<string, string>());
  const [currentMs, setCurrentMs] = useState(0);
  const [totalMs, setTotalMs] = useState(0);

  // One AudioContext + engine per editor mount.
  useEffect(() => {
    const ctx = new AudioContext();
    const engine = new PlaybackEngine(ctx, (state) => {
      useCompositionStore.getState().setPlayback({ state });
    });
    engineRef.current = engine;
    const keys = loadedKeys.current;
    return () => {
      engine.dispose();
      void ctx.close();
      engineRef.current = null;
      keys.clear();
    };
  }, []);

  // Load sections whose generated audio is new or changed, and wire the
  // materialized stem mix into each section's audio graph. A section without
  // audio simply does not enter the engine.
  //
  // The engine wires one gain/EQ chain per stem at load time, so a section that
  // gains a stem is reloaded (stems are part of the load key). After a load —
  // and on every `sections` change, including a pure volume/EQ tweak that does
  // not change the load key — `applyStemMix` pushes each stem's mix to the
  // engine. `applyStemMix` stores the mix on the section graph, so `buildGraph`
  // reapplies it after every pause/play rebuild and a mute survives transport
  // changes.
  useEffect(() => {
    const engine = engineRef.current;
    if (!engine || !projectId) return;
    let cancelled = false;
    const applyMixes = (section: (typeof sections)[number]): void => {
      for (const [stem, mix] of Object.entries(section.stems)) {
        engine.applyStemMix(section.id, stem, {
          muted: mix.muted,
          volume: mix.volume,
          eq: { lowDb: mix.eq.lowDb, midDb: mix.eq.midDb, highDb: mix.eq.highDb },
        });
      }
    };
    void (async () => {
      for (const section of sections) {
        if (!section.audio) continue;
        const stems = Object.keys(section.stems).sort();
        const loadKey = `${section.audio.assetHash}|${stems.join(",")}`;
        if (loadedKeys.current.get(section.id) === loadKey) {
          // Already loaded with this stem set — only the mix may have changed.
          applyMixes(section);
          continue;
        }
        try {
          const res = await fetch(assetUrl(projectId, section.audio.assetHash));
          if (cancelled) return;
          const bytes = await res.arrayBuffer();
          if (cancelled) return;
          await engine.loadSection(section.id, bytes, stems, section.order);
          if (cancelled) return;
          loadedKeys.current.set(section.id, loadKey);
          // Reapply the mix: loadSection rebuilds the graph with empty mixes.
          applyMixes(section);
          setTotalMs(engine.getTotalMs());
        } catch {
          // A failed asset fetch just leaves that section out of the timeline;
          // the rest of the song still plays.
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, sections]);

  // Drive the playhead with requestAnimationFrame while playing. Kept in local
  // component state — updating the store every frame would flood subscribers.
  useEffect(() => {
    if (playbackState !== "playing") return;
    let frame = 0;
    const tick = (): void => {
      const engine = engineRef.current;
      if (engine) setCurrentMs(engine.getCurrentMs());
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playbackState]);

  // After the song ends (engine.finish -> "stopped"), snap the playhead to 0.
  useEffect(() => {
    if (playbackState === "stopped") setCurrentMs(0);
  }, [playbackState]);

  return {
    play: () => void engineRef.current?.play(),
    pause: () => engineRef.current?.pause(),
    seek: (ms: number) => {
      engineRef.current?.seek(ms);
      setCurrentMs(engineRef.current?.getCurrentMs() ?? ms);
    },
    currentMs,
    totalMs,
    engineRef,
  };
}
